import { categoryOf, describeCostType } from '../helpers/procurementCategories';

// Pure data shaping for the Uncommitted tab. The grouping rules matter more than
// usual here because the three API result sets are at three different grains —
// see functions/src/sql/readUncommittedRollup.sql. Keeping this out of the
// component lets the "never double-count ETC" rule be tested directly.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Roll the (Job, Cost Type) rows up by a chosen dimension.
 *
 * ETC and Open Commitments both exist at this grain, so both are summed. This is
 * the only grain at which Uncommitted is exact.
 */
export const rollupBy = (groups = [], dimension = 'Category') => {
    const keyOf = (r) => {
        if (dimension === 'Category') return categoryOf(r.Cost_Type_Code) || '(uncategorized)';
        if (dimension === 'Cost_Type_Code') return r.Cost_Type_Code || '(none)';
        if (dimension === 'Job_Number') return r.Job_Number || '(none)';
        return r[dimension] || '(none)';
    };

    const map = new Map();
    groups.forEach((r) => {
        const key = keyOf(r);
        const cur = map.get(key) || { key, etc: 0, openCommitted: 0, uncommitted: 0, rows: 0 };
        cur.etc += num(r.ETC);
        cur.openCommitted += num(r.OpenCommitted);
        cur.uncommitted += num(r.Uncommitted);
        cur.rows += 1;
        map.set(key, cur);
    });

    return [...map.values()].sort((a, b) => b.uncommitted - a.uncommitted);
};

/**
 * Resource-code rollup. Deliberately reports the COMMITMENT side only.
 *
 * ETC rows carry no resource code (40 of ~1.2M do), so there is no honest way to
 * show ETC or Uncommitted per resource. Splitting ETC across resource codes would
 * be inventing numbers, and joining it would repeat each ETC amount once per
 * resource and inflate the totals. The view therefore shows what is committed per
 * resource code, and Uncommitted stays at the cost-type grain where it is real.
 */
export const rollupByResource = (detail = []) => {
    const map = new Map();
    detail.forEach((r) => {
        const code = r.Resource_Code || '';
        const key = code || '(no resource code)';
        const cur = map.get(key) || {
            key,
            resourceCode: code,
            tier: r.Tier || '',
            category: categoryOf(r.Cost_Type_Code) || '',
            openCommitted: 0,
            rows: 0,
        };
        cur.openCommitted += num(r.OpenCommitted);
        cur.rows += 1;
        if (!cur.tier && r.Tier) cur.tier = r.Tier;
        map.set(key, cur);
    });
    return [...map.values()].sort((a, b) => b.openCommitted - a.openCommitted);
};

/** Job × cost-type detail rows for the expanded view, richest first. */
export const jobRows = (groups = []) => groups
    .map((r) => ({
        jobNumber: r.Job_Number,
        costTypeCode: r.Cost_Type_Code,
        costTypeLabel: describeCostType(r.Cost_Type_Code),
        category: categoryOf(r.Cost_Type_Code) || '(uncategorized)',
        etc: num(r.ETC),
        openCommitted: num(r.OpenCommitted),
        uncommitted: num(r.Uncommitted),
    }))
    .sort((a, b) => b.uncommitted - a.uncommitted);

// ---------------------------------------------------------------------------
// ETC line detail — result set (4). One row per forecast line, which is the
// grain buyers work at: they chase a specific PO on a specific job, not a
// cost-type rollup. Nothing is joined to the commitment side here, so these
// rows sum back exactly to the ETC column of the rollup.
// ---------------------------------------------------------------------------

/**
 * Result set (5) → lookup keyed by job + PO. CurrentPOAmount is actual cost to
 * date plus open commitment — what the PO stands at today.
 */
// PO numbers arrive in three shapes — zero-padded from cost history, numeric
// from the open-PO view, free-typed on forecast lines — so both sides of the
// lookup reduce to the plain numeric form ('00215840' -> '215840').
export const normPo = (po) => {
    const s = String(po == null ? '' : po).trim();
    if (!s) return '';
    return /^\d+$/.test(s) ? String(Number(s)) : s;
};

export const poAmountMap = (poAmounts = []) => {
    const map = new Map();
    poAmounts.forEach((r) => {
        const key = `${r.Job_Number}|${normPo(r.PO_Number)}`;
        const prev = map.get(key) || { actual: 0, open: 0, current: 0 };
        map.set(key, {
            actual: prev.actual + num(r.ActualCost),
            open: prev.open + num(r.OpenAmount),
            current: prev.current + num(r.CurrentPOAmount),
        });
    });
    return map;
};

/** Normalize the raw line rows into the shape the table renders. */
export const etcLines = (lines = [], poMap = new Map()) => lines.map((r, i) => ({
    id: `${r.Job_Number}-${r.Cost_Type_Code}-${r.Cost_Code}-${r.PO_Number}-${i}`,
    jobNumber: r.Job_Number || '',
    jobName: r.Job_Name || '',
    poNumber: r.PO_Number || '',
    notes: r.Notes || '',
    resourceCode: r.Resource_Code || '',
    supplierName: r.Supplier_Name || '',
    costTypeCode: r.Cost_Type_Code || '',
    costTypeLabel: describeCostType(r.Cost_Type_Code),
    costCode: r.Cost_Code || '',
    costCodeLabel: r.Cost_Code_Description || '',
    category: categoryOf(r.Cost_Type_Code) || '(uncategorized)',
    // Burn-tool phase window ('Burn Tool Labor Phased'), when the job carries
    // one for this cost code — shown under the cost code in the table.
    phaseStart: r.Phase_Start || null,
    phaseEnd: r.Phase_End || null,
    // Burn window for the line's cost type: the equipment burn tool's span for
    // 52xxx lines, the labor burn tool's span otherwise — under the cost type.
    typeStart: r.Type_Start || null,
    typeEnd: r.Type_End || null,
    tab: r.Forecast_Tab || '',
    etc: num(r.ETC),
    // Current PO amount (actual + open) — null, not 0, when the line has no PO
    // or the PO has no cost rows, so the table can show a blank instead of 0.00.
    currentPo: r.PO_Number && poMap.has(`${r.Job_Number}|${normPo(r.PO_Number)}`)
        ? poMap.get(`${r.Job_Number}|${normPo(r.PO_Number)}`).current
        : null,
}));

/** Distinct values for the filter chips, each with a row count. */
export const lineFilterOptions = (rows = []) => {
    const tally = (pick) => {
        const map = new Map();
        rows.forEach((r) => {
            const v = pick(r);
            if (!v) return;
            map.set(v, (map.get(v) || 0) + 1);
        });
        return [...map.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count);
    };
    return {
        categories: tally((r) => r.category),
        costTypes: tally((r) => r.costTypeCode),
        teams: [],
    };
};

/**
 * Apply the table filters.
 *
 * `search` matches across every text column at once — a buyer typing a supplier
 * name, a PO number or a job number should not first have to say which one it
 * is. `po` narrows to lines that do or don't carry a PO number, which is the
 * distinction the whole view exists to surface.
 */
export const filterLines = (rows = [], filters = {}) => {
    const q = String(filters.search || '').trim().toLowerCase();
    const cats = filters.categories || [];
    const types = filters.costTypes || [];
    const po = filters.po || 'all'; // 'all' | 'with' | 'without'
    const min = Number.isFinite(parseFloat(filters.minEtc)) ? parseFloat(filters.minEtc) : null;

    return rows.filter((r) => {
        if (cats.length && !cats.includes(r.category)) return false;
        if (types.length && !types.includes(r.costTypeCode)) return false;
        if (po === 'with' && !r.poNumber) return false;
        if (po === 'without' && r.poNumber) return false;
        if (min !== null && Math.abs(r.etc) < min) return false;
        if (!q) return true;
        return [
            r.jobNumber, r.jobName, r.poNumber, r.notes, r.resourceCode, r.supplierName,
            r.costTypeCode, r.costTypeLabel, r.costCode, r.costCodeLabel, r.tab,
        ].some((v) => String(v).toLowerCase().includes(q));
    });
};

/**
 * Sort by any column. Amounts sort by magnitude-aware numeric compare, text
 * sorts case-insensitively, and blanks always sink to the bottom regardless of
 * direction — an empty PO number is not "the smallest PO number".
 */
export const sortLines = (rows = [], key = 'etc', dir = 'desc') => {
    const mult = dir === 'asc' ? 1 : -1;
    const numeric = key === 'etc' || key === 'currentPo';
    return [...rows].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (numeric) {
            // null means "no PO amount", not zero — sink it like a text blank.
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return (Number(av) - Number(bv)) * mult;
        }
        const as = String(av ?? '').trim();
        const bs = String(bv ?? '').trim();
        if (!as && !bs) return 0;
        if (!as) return 1;
        if (!bs) return -1;
        return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' }) * mult;
    });
};

/** ETC total for whatever subset is currently on screen. */
export const lineTotal = (rows = []) => rows.reduce((a, r) => a + num(r.etc), 0);

/**
 * Totals plus the data-quality signals the view shows alongside them, so the
 * headline number is never presented without its caveats.
 */
export const totals = (groups = [], detail = [], outliers = []) => {
    const etc = groups.reduce((a, r) => a + num(r.ETC), 0);
    const openCommitted = groups.reduce((a, r) => a + num(r.OpenCommitted), 0);
    const resolved = detail.filter((r) => r.Resource_Code).length;

    return {
        etc,
        openCommitted,
        uncommitted: etc - openCommitted,
        jobs: new Set(groups.map((r) => r.Job_Number)).size,
        // Free-text ETC cells that are not numbers at all (e.g. "1 Month- 2 weeks
        // on Excavation"). They contribute nothing to the total.
        nonNumericEtcRows: groups.reduce((a, r) => a + num(r.NonNumericEtcRows), 0),
        etcRowsWithoutPhase: groups.reduce((a, r) => a + num(r.EtcRowsWithoutPhase), 0),
        resourceRowsResolved: resolved,
        resourceRowsTotal: detail.length,
        largestEtcEntries: outliers.slice(0, 10).map((o) => ({
            jobNumber: o.Job_Number,
            tab: o.Forecast_Tab,
            label: o.Row_Label || o.Forecast_Row_ID,
            etc: num(o.ETC),
        })),
    };
};
